import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { MatchingProfile, MatchingProfileReason } from '@sondermind/utilities/models-matching';

export const READINESS_OPTIONS = {
  contemplation: {
    title: 'Exploration',
    info: 'I\'m still exploring how therapy works and deciding if it\'s right for me.',
    directReferralInfo: 'I am just starting to explore my options.'
  },
  preparation: {
    title: 'Planning',
    info: 'I\'m interested in getting therapy and am deciding if I should go with SonderMind.',
    directReferralInfo: null
  },
  action: {
    title: 'Action',
    info: 'I\'m ready to find a therapist and schedule a session.',
    directReferralInfo: 'I am ready to find a therapist and schedule a session.'
  }
};

export const THERAPY_TOPICS_CONTEXT = {
  Anxiety: {
    title: 'Anxiety',
    info: 'I\'m feeling anxious or worried.'
  },
  Depression: {
    title: 'Depression',
    info: 'I\'m feeling down or depressed.'
  },
  Trauma: {
    title: 'Trauma',
    info: 'I\'m struggling with a past traumatic experience.'
  },
  'General support': {
    title: 'General support',
    info: 'I want general support.'
  },
  'Medication evaluation': {
    title: 'Medication evaluation',
    info: 'I need guidance finding the right medication.'
  }
};

export enum TimePreferences {
  WEEKDAY_MORNING = 'weekdaysMorning',
  WEEKDAY_DAYTIME = 'weekdaysDaytime',
  WEEKDAY_EVENING = 'weekdaysEvening',
  WEEKEND_DAYTIME = 'weekendsDaytime',
  WEEKEND_EVENING = 'weekendsEvening',
  NO_PREFERENCE = 'noPreference'
}

export class ClientReferral {
  id: number;
  platformIdentity: string;
  chatProfileUuid: string;
  userId: number;
  aasmState: string;
  genericReferralDescription: string;
  notifiedAt: string;
  declinedAt: string;
  acceptedAt: string;
  callRequestedAt: string;
  note: string;
  status: string;
  contactBirthdate: string;
  contactFirstName: string;
  contactLastName: string;
  displayFirstName: string;
  displayName: string;
  gender: string;
  contactEmail: string;
  contactPhone: string;
  contactCity: string;
  contactState: string;
  contactSmsAllowed: string;
  insuranceName: string;
  insurancePlanName: string;
  insuranceNumber: string;
  insuranceGroup: string;
  insuranceHolderFirstName: string;
  insuranceHolderLastName: string;
  reasonOther: string;
  reason: string;
  reasons: string[];
  clients: any;
  clientVerificationPending: boolean;
  automatch: boolean;
  personaClientId: number;
  clientInsurances: any;
  matchingProfile: MatchingProfile | null;
  tagNames: string[];
  communicationPreferences: CommunicationPreference[];
  requiresCreditCard: boolean;
  requiresInsurance: boolean;
  hasCreditCard: boolean;
  hasCommercialInsurance: boolean;
  hasSelfPay: boolean;
  hasEapInsurance: boolean;
  loggedIn: boolean;
  lastActivityAt: string;
  readiness: string;
  genderText: string;
  referralSource: string;
  referringReason: string;
  referralState: string;
  source: string;
  urgent: boolean;
  skipAcknowledgeInfo: boolean;
  preferredAvailability: TimePreferences[];

  constructor(json: IClientReferralApiResponse) {
    this.id = json.id;
    this.platformIdentity = json.platform_identity;
    this.chatProfileUuid = json.chat_profile_uuid;
    this.userId = json.user_id;
    this.aasmState = json.aasm_state;
    this.genericReferralDescription = json.generic_referral_description;
    this.notifiedAt = json.notified_at;
    this.declinedAt = json.declined_at;
    this.acceptedAt = json.accepted_at;
    this.callRequestedAt = json.call_requested_at;

    this.contactBirthdate = json.contact_birthdate;
    this.contactFirstName = json.contact_first_name;
    this.contactLastName = json.contact_last_name;
    this.displayFirstName = json.display_first_name;
    this.displayName = json.display_name;
    this.contactEmail = json.contact_email;
    this.gender = json.gender;
    this.contactPhone = json.contact_phone;
    this.contactCity = json.lat_lng_city;
    this.contactState = json.lat_lng_state;
    this.contactSmsAllowed = json.contact_sms_allowed;
    this.insuranceName = json.insurance_name;
    this.insurancePlanName = json.insurance_plan_name;
    this.insuranceNumber = json.insurance_number;
    this.insuranceGroup = json.insurance_group;
    this.insuranceHolderFirstName = json.insurance_holder_first_name;
    this.insuranceHolderLastName = json.insurance_holder_last_name;
    this.reasons = json.reasons;
    this.reason = this.reasons.join(', ');
    this.reasonOther = json.reason_other;
    this.clients = json.clients;
    this.clientVerificationPending = json.client_verification_pending;
    this.automatch = json.automatch;
    this.personaClientId = json.persona_client_id;
    this.clientInsurances = json.client_insurances;
    this.matchingProfile = json.matching_profile ? new MatchingProfile().load_from_json(json.matching_profile) : null;
    this.tagNames = json.tag_names ? json.tag_names : [];
    this.communicationPreferences = json.communication_preferences ?? [];
    this.requiresCreditCard = json.requires_credit_card;
    this.requiresInsurance = json.requires_insurance;
    this.hasCreditCard = json.has_credit_card;
    this.hasCommercialInsurance = json.has_commercial_insurance;
    this.hasSelfPay = json.has_self_pay;
    this.hasEapInsurance = json.has_eap_insurance;
    this.loggedIn = json.logged_in;
    this.lastActivityAt = json.last_activity_at;
    this.readiness = json.readiness;
    this.genderText = this.getGenderText(json.gender);
    this.note = json.note;
    this.status = json.status;
    this.referralSource = json.referral_source;
    this.referringReason = json.referring_reason;
    this.referralState = json.referral_state;
    this.source = json.source;
    this.urgent = json.urgent;
    this.skipAcknowledgeInfo = json.skip_acknowledge_info;
    this.preferredAvailability = json.preferred_availability;
  }

  get matchingProfileReasons(): MatchingProfileReason[] {
    return this.matchingProfile ? this.matchingProfile.reasons : [];
  }

  get readinessWithInfo(): IReadinessInfo | null {
    if (this.readiness) {
      return READINESS_OPTIONS[this.readiness];
    }

    return null;
  }

  getGenderText(gender: string): string {
    return gender === 'Prefer not to answer' || gender === null ? 'Did not provide gender' : gender;
  }
}

export interface IClientReferralApiResponse {
  /* eslint-disable @typescript-eslint/naming-convention */
  id: number;
  platform_identity: string;
  chat_profile_uuid: string;
  user_id: number;
  aasm_state: string;
  generic_referral_description: string;
  notified_at: string;
  declined_at: string;
  accepted_at: string;
  call_requested_at: string;
  note: string;
  status: string;
  contact_birthdate: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  display_first_name: string;
  display_name: string;
  lat_lng_city: string;
  lat_lng_state: string;
  contact_sms_allowed: string;
  gender: string;
  insurance_name: string;
  insurance_plan_name: string;
  insurance_number: string;
  insurance_group: string;
  insurance_holder_first_name: string;
  insurance_holder_last_name: string;
  reasons: string[];
  reason_other: string;
  clients: any;
  client_verification_pending: boolean;
  automatch: boolean;
  persona_client_id: number;
  client_insurances: any;
  matching_profile: MatchingProfile;
  tag_names: string[];
  communication_preferences: CommunicationPreference[];
  requires_credit_card: boolean;
  requires_insurance: boolean;
  has_credit_card: boolean;
  has_commercial_insurance: boolean;
  has_self_pay: boolean;
  has_eap_insurance: boolean;
  logged_in: boolean;
  last_activity_at: string;
  readiness: string;
  referral_source: string;
  referring_reason: string;
  referral_state: string;
  source: string;
  urgent: boolean;
  skip_acknowledge_info: boolean;
  preferred_availability: TimePreferences[];
  /* eslint-enable @typescript-eslint/naming-convention */
}

export interface IReadinessInfo {
  title: string;
  info: string;
  directReferralInfo: string | null;
}

export interface IReasonInfo {
  title: string;
  info: string;
}
